import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Box, Typography } from "@material-ui/core";
import { Colors, ScorecardConstants } from "constants/constants";
import { handleGetRoles } from "../requests";
import { useStyles } from "./styles";
import { useDateWeekSelector } from "hooks/useDateWeekSelector";
import { StationSelect } from "componentsNew/elements/SelectField";
import { SearchTypeSelect } from "components/elements/SearchTypeSelect";
import { MonthSelect } from "components/elements/MonthSelect";
import { WeekSelect } from "components/elements/WeekSelect";
import { YearSelect } from "components/elements/YearSelect";
import { ScorecardOpenButton } from "components/buttons/ScorecardOpenButton";
import { ScorecardCompareButton } from "components/buttons/ScorecardCompareButton";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { useDispatch } from "react-redux";
import {
  ScorecardExternalSetDate,
  ScorecardSetSelectType,
  ScorecardSetStation,
} from "actions/scorecardActions";
import { DatePickWithArrow } from "components/elements/DatePickWithArrow";
import { daRoles } from "mocks/roleDA";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type ElementData = { counter: number; title: string; role: number };

export const RolesSplit = ({ withModal = true, withCompare = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { userData } = useTypeSelectors((state) => state.auth);
  const { externalSettingActive, externalSettings, rolesSettings } = useTypeSelectors(
    (state) => state.scoreCardSettings,
  );
  const defaultStationCondition = userData?.station_spots.some(
    (el: { station: { title: string; id: number } }) => el.station.id === 1,
  )
    ? 1
    : userData?.station_spots[0]?.station?.id;
  const [compareStation, setCompareStation] = useState(`${defaultStationCondition}`);
  const [requestData, setRequestData] = useState<ElementData[]>([]);

  const handleChangeStation = (data: string) =>
    !withCompare
      ? setCompareStation(data)
      : dispatch(ScorecardSetStation(data, ScorecardConstants.ROLES_SET_CURRENT_STATION));
  const handleChangeSelectType = (select: string) =>
    dispatch(ScorecardSetSelectType(select, ScorecardConstants.ROLES_SET_SELECT_TYPE));
  const handleChangeWeek = (week: string) =>
    dispatch(
      ScorecardExternalSetDate({ ...rolesSettings?.date, week }, ScorecardConstants.ROLES_SET_DATE),
    );
  const handleChangeMonth = (month: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...rolesSettings?.date, month },
        ScorecardConstants.ROLES_SET_DATE,
      ),
    );
  const handleChangeYear = (year: string) =>
    dispatch(
      ScorecardExternalSetDate({ ...rolesSettings?.date, year }, ScorecardConstants.ROLES_SET_DATE),
    );

  const handleChangeDayDate = (date: string) =>
    dispatch(
      ScorecardExternalSetDate(
        { ...rolesSettings?.date, day: date },
        ScorecardConstants.RETURN_SET_DATE,
      ),
    );

  const {
    week,
    month,
    year,
    selectType,
    date,
    onTypeChange,
    dateCondition,
    dateConditionMonth,
    onWeekReduce,
    onWeekIncrease,
    onMonthReduce,
    onMonthIncrease,
    onYearReduce,
    onYearIncrease,
    dateConditionYear,
    onDateChange,
    onDateChangeByDay,
  } = useDateWeekSelector({
    defaultWeek: rolesSettings?.date?.week,
    defaultMonth: rolesSettings?.date?.month,
    defaultYear: rolesSettings?.date?.year,
    defaultSelectType: rolesSettings?.selectType,
    onSelectTypeSet: handleChangeSelectType,
    onWeekSet: handleChangeWeek,
    onMonthSet: handleChangeMonth,
    onYearSet: handleChangeYear,
    onDateSet: handleChangeDayDate,
  });

  const conditionLocalStation = !withCompare ? compareStation : rolesSettings?.station;

  useEffect(() => {
    if (
      (conditionLocalStation && (month || week || year || date)) ||
      (externalSettings?.station && externalSettings?.selectType && externalSettings?.date)
    ) {
      const params = externalSettingActive
        ? {
            station: externalSettings?.station,
            ...(externalSettings?.selectType === "month" && {
              month: +externalSettings?.date?.month,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "week" && {
              week: +externalSettings?.date?.week,
              year: +externalSettings?.date?.year,
            }),
            ...(externalSettings?.selectType === "day" && { day: externalSettings?.date?.day }),
          }
        : {
            station: conditionLocalStation,
            ...(selectType === "month" && { year, month }),
            ...(selectType === "week" && { year, week }),
            ...(selectType === "annual" && { year }),
            ...(selectType === "day" && { day: date }),
          };
      handleGetRoles(params, setRequestData);
    }
  }, [
    rolesSettings?.station,
    month,
    week,
    selectType,
    year,
    date,
    externalSettingActive,
    externalSettings?.station,
    externalSettings?.selectType,
    externalSettings?.date,
    conditionLocalStation,
  ]);

  const roles = [
    daRoles.training,
    daRoles.quality,
    daRoles.rescue,
    daRoles.standby,
    daRoles.special,
    daRoles.driver,
  ].map((role) => ({
    name: role.name,
    id: role.roleId,
  }));
  const labels = roles.map((el) => el.name);
  const newData: number[] = roles.map((role) => {
    const matchingData = requestData.find((dataItem: ElementData) => role.id === dataItem.role);
    return matchingData?.counter ?? 0;
  });
  const dataChart = {
    labels,
    datasets: [
      {
        label: "Counter",
        data: newData,
        backgroundColor: [
          Colors.PASTEL_BLUE,
          Colors.PASTEL_GREEN,
          Colors.PASTEL_ORANGE,
          Colors.PASTEL_PINK,
          Colors.PASTEL_RED,
          Colors.PASTEL_VIOLET,
          Colors.PASTEL_YELLOW,
          Colors.PASTEL_TURQUOISE,
          Colors.PASTEL_SAND,
          Colors.PASTEL_PINE_TREE,
        ],
        minBarLength: 3,
        barThickness: 15,
        borderRadius: 25,
      },
    ],
  };

  return (
    <Box className={classes.root}>
      {!externalSettingActive && (
        <Box className={classes.head}>
          <Box className={classes.box}>
            <StationSelect
              value={!withCompare ? compareStation : rolesSettings?.station}
              onChange={handleChangeStation}
            />
            <SearchTypeSelect
              value={selectType}
              onChange={onTypeChange}
              options={[
                { id: "week", title: "Week" },
                { id: "month", title: "Month" },
                { id: "annual", title: "Annual" },
                { id: "day", title: "Day" },
              ]}
            />
            {selectType === "month" && (
              <MonthSelect
                month={month}
                year={year}
                onNext={onMonthIncrease}
                onPrev={onMonthReduce}
                disabled={dateConditionMonth()}
              />
            )}
            {selectType === "week" && (
              <WeekSelect
                week={week}
                year={year}
                onNext={onWeekIncrease}
                onPrev={onWeekReduce}
                disabled={dateCondition()}
              />
            )}
            {selectType === "annual" && (
              <YearSelect
                year={year}
                onNext={onYearIncrease}
                onPrev={onYearReduce}
                disabled={dateConditionYear()}
              />
            )}
            {selectType === "day" && (
              <DatePickWithArrow
                value={date}
                onChange={onDateChange}
                changeDay={onDateChangeByDay}
                displayWeek
              />
            )}
          </Box>
          <Box>
            {withCompare && (
              <ScorecardCompareButton
                children={<RolesSplit withModal={false} withCompare={false} />}
                children2={<RolesSplit withModal={false} withCompare={false} />}
              />
            )}
            {withModal && <ScorecardOpenButton children={<RolesSplit withModal={false} />} />}
          </Box>
        </Box>
      )}
      <Typography variant="body2" style={{ width: "100%", textAlign: "center" }}>
        Roles
      </Typography>
      <Bar options={options} data={dataChart} />
    </Box>
  );
};

const options = {
  scale: {
    ticks: {
      precision: 0,
    },
  },
  scales: {
    y: {
      title: {
        display: false,
        text: "Value",
      },
      min: 0,
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
    x: {
      ticks: {
        color: `${Colors.MAIN}`,
        font: {
          weight: "700",
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top" as const,
      display: false,
    },
    title: {
      display: false,
      text: "Roles",
    },
  },
};
